import { Text } from 'src/components/ui';
import { createColumnHelper } from '@tanstack/react-table';
import Chip from 'src/components/common/Chip';

import { ICampaignsListingItem, IGroup } from 'src/lib/schemas';
import CampaignOptionsMenu from './CampaignOptionsMenu';

export interface ICampaignsListingColumn extends ICampaignsListingItem {
	action?: unknown;
	options?: unknown;
}

const columnHelper = createColumnHelper<ICampaignsListingColumn>();

interface IGetCampaignsListingColumns {
	onViewCampaign: (id: string) => void;
	onDeleteCampaign: (id: string) => Promise<void>;
	onDuplicateCampaign: (id: string) => Promise<void>;
	onDeactivateCampaign: (id: string) => Promise<void>;
	groups: IGroup[];
	simplified?: boolean;
}

export const getCampaignsListingColumns = ({
	onViewCampaign,
	onDeleteCampaign,
	onDuplicateCampaign,
	onDeactivateCampaign,
	groups,
	simplified,
}: IGetCampaignsListingColumns) => {
	if (simplified)
		return [
			columnHelper.accessor('title', {
				header: 'Campaign Title',
				cell: (info) => {
					const value = info.getValue();
					const title = value?.trim();
					return (
						<Text fontStyle={!title ? 'italic' : 'normal'}>
							{!title ? 'No title' : title}
						</Text>
					);
				},
			}),
			columnHelper.accessor('options', {
				header: '',
				meta: {
					ThProps: {
						width: '50px',
					},
					TdProps: {
						textAlign: 'center',
					},
				},
			cell: (info) => (
				<CampaignOptionsMenu
					onEdit={() => onViewCampaign(info.row.original.id)}
					onDuplicate={() => onDuplicateCampaign(info.row.original.id)}
					onDelete={() => onDeleteCampaign(info.row.original.id)}
					onDeactivate={() => onDeactivateCampaign(info.row.original.id)}
					status={info.row.original.status as 'draft' | 'published' | 'archived' | 'finalized'}
					campaignTitle={info.row.original.title}
				/>
			),
		}),
	];
	else {
		return [
			columnHelper.accessor('group', {
				header: 'Campaign Group',
				cell: (info) => {
					const groupId = info.row.original.group;
					const group = groups.find((g) => g.id === groupId);
					return group?.name ?? '';
				},
			}),
			columnHelper.accessor('title', {
				header: 'Campaign Title',
				cell: (info) => {
					const value = info.getValue();
					const title = value?.trim();
					return (
						<Text fontStyle={!title ? 'italic' : 'normal'}>
							{!title ? 'No title' : title}
						</Text>
					);
				},
			}),
		columnHelper.accessor('status', {
			header: 'Status',
			cell: (info) => {
				const status = info.getValue();
				const getStatusLabel = () => {
					switch (status) {
						case 'draft': return 'Draft';
						case 'published': return 'Published';
						case 'finalized': return 'Finalized';
						case 'archived': return 'Archived';
						default: return status;
					}
				};
				const getStatusColor = () => {
					switch (status) {
						case 'draft': return 'gray';
						case 'published': return 'green';
						case 'finalized': return 'blue';
						case 'archived': return 'red';
						default: return 'gray';
					}
				};
				return (
					<Chip
						label={getStatusLabel()}
						tagProps={{
							colorScheme: getStatusColor(),
							variant: 'outline',
						}}
					/>
				);
			},
		}),
		columnHelper.accessor('options', {
			header: '',
			meta: {
				ThProps: {
					width: '50px',
				},
				TdProps: {
					textAlign: 'center',
				},
			},
			cell: (info) => (
				<CampaignOptionsMenu
					onEdit={() => onViewCampaign(info.row.original.id)}
					onDuplicate={() => onDuplicateCampaign(info.row.original.id)}
					onDelete={() => onDeleteCampaign(info.row.original.id)}
					onDeactivate={() => onDeactivateCampaign(info.row.original.id)}
					status={info.row.original.status as 'draft' | 'published' | 'archived' | 'finalized'}
					campaignTitle={info.row.original.title}
				/>
			),
		}),
	];
}
};
